import DirectWebSocketService from "./directWebSocketService";
import { getWebSocketUrl } from "src/config/websocketConfig";

/**
 * Manages WebSocket connections per browser tab using singleton pattern.
 * Enables long-lived connections that can handle multiple chat messages.
 * Each tab gets its own WebSocketManager instance and connection.
 */
class WebSocketManager {
  private static instance: WebSocketManager;
  private wsService: DirectWebSocketService | null = null;

  private constructor() {
    this.setupCleanupHandlers();
  }

  /**
   * Returns singleton instance per browser tab.
   * First call creates new instance + WebSocket connection.
   * Subsequent calls reuse existing instance + connection.
   * This enables multiple messages to use the same WebSocket connection.
   */
  static getInstance(): WebSocketManager {
    if (!WebSocketManager.instance) {
      WebSocketManager.instance = new WebSocketManager();
    }
    return WebSocketManager.instance;
  }

  /**
   * Returns active WebSocket connection, creating or reconnecting as needed.
   * Connection is reused across multiple chat messages in the same tab session.
   */
  async getConnection(): Promise<DirectWebSocketService> {
    if (!this.wsService) {
      const wsUrl = getWebSocketUrl();
      this.wsService = new DirectWebSocketService(wsUrl);
    }

    // Always ensure connection is active
    if (!this.wsService.isConnected()) {
      await this.wsService.connect();
    }

    return this.wsService;
  }

  /**
   * Closes WebSocket connection and cleans up resources.
   */
  disconnect(): void {
    if (this.wsService) {
      this.wsService.disconnect();
      this.wsService = null;
    }
  }

  /**
   * Sets up cleanup handlers to properly close connection when tab closes.
   */
  private setupCleanupHandlers(): void {
    // Handle page unload/refresh
    window.addEventListener("beforeunload", () => {
      this.disconnect();
    });
  }
}

export default WebSocketManager;
